import { forwardRef, useCallback, useState } from 'react'
import { alpha, Box, BoxProps, Menu, MenuItem, SvgIcon, Typography } from '@mui/material'

import { isHappa } from '@/hooks'
import { useLocaleStore } from '@/store/locale'
import { LANGUAGES } from '@/constants'

const DEFAULT_LOCALE = LANGUAGES[0]

const LANG_MAP: Record<string, string> = {
  it: 'Ita',
  en: 'Eng'
}
const formatLang = (lang: string) => LANG_MAP[lang.toLowerCase()] || lang.toUpperCase().slice(0, 3)

const LanguageSwitcher = forwardRef<HTMLElement, BoxProps>(({ sx, ...props }, ref) => {
  const currentLocale = useLocaleStore((state) => state.currentLocale)
  const currentTranslations = useLocaleStore((state) => state.currentTranslations)

  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null)
  const langMenuOpen = Boolean(langAnchorEl)

  const handleLangClick = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget)
  }

  const handleLangClose = () => {
    setLangAnchorEl(null)
  }

  const getLangHref = useCallback(
    (lang: string) => {
      const translation = currentTranslations?.find((t: { locale: string }) =>
        t.locale.includes(lang)
      )
      if (translation?.href) {
        return lang === DEFAULT_LOCALE
          ? translation.href.replace(new RegExp(`^/${lang}(/|$)`), '/')
          : translation.href
      }
      return lang === DEFAULT_LOCALE ? '/' : `/${lang}`
    },
    [currentTranslations]
  )

  return (
    <>
      <Box
        ref={ref}
        onClick={handleLangClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.8rem',
          cursor: 'pointer',
          padding: (theme) => `1.2rem ${theme.spacing(3)}`,
          borderRadius: (theme) =>
            isHappa ? theme.shape.borderRadius1 : theme.shape.borderRadius4,
          backgroundColor: (theme) =>
            isHappa ? 'transparent' : alpha(theme.palette.primary.main, 0.06),
          color: (theme) => theme.palette.primary.main,
          ...sx
        }}
        {...props}
      >
        <Typography variant="menu" component="span" fontWeight={500} sx={{ minWidth: '3rem' }}>
          {formatLang(currentLocale || LANGUAGES[0])}
        </Typography>
        <SvgIcon
          sx={{
            transform: langMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}
        >
          <use href="#ico-arrow-down" />
        </SvgIcon>
      </Box>

      <Menu
        anchorEl={langAnchorEl}
        open={langMenuOpen}
        onClose={handleLangClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: '0.8rem' }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              backgroundColor: theme.palette.text.primaryContrastText,
              borderRadius: isHappa ? theme.shape.borderRadius1 : theme.shape.borderRadius4,
              boxShadow: 'none',
              '& ul': {
                backgroundColor: alpha(theme.palette.primary.main, 0.06),
                padding: 0
              }
            })
          }
        }}
      >
        {LANGUAGES.map((lang: string) => (
          <MenuItem
            key={lang}
            component="a"
            href={getLangHref(lang)}
            onClick={handleLangClose}
            selected={currentLocale === lang}
            sx={(theme) => ({
              textDecoration: 'none',
              padding: `1.2rem ${theme.spacing(3)}`,
              color: theme.palette.primary.main,
              transition: `all 0.3s ${theme.customEasings.outCubic}`,
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main,
                color: `${theme.palette.text.primaryContrastText} !important`,
                pointerEvents: 'none'
              },
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08)
              }
            })}
          >
            <Typography variant="menu" fontWeight={500} sx={{ pr: '2.4rem', minWidth: '3rem' }}>
              {formatLang(lang)}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
})

export default LanguageSwitcher
